import React, { useEffect, useMemo, useState } from "react";
import {
  Wallet,
  CalendarDays,
  CheckCircle2,
  Clock3,
  Pencil,
  Trash2,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { notify } from "../utils/notify";
import {
  collection,
  getDocs,
  limit,
  orderBy,
  query,
} from "firebase/firestore";
import { db } from "../firebase";
import { createPaymentWithStats, deletePaymentWithStats, updatePaymentWithStats } from "../services/financeWriteService";
import { usePanelTheme } from "../context/themeContext";
import StatCard from "../components/StatCard";
import UIButton from "../components/UIButton";
import PageShell from "../components/PageShell";
import PanelCard from "../components/PanelCard";
import SectionHeading from "../components/SectionHeading";

function formatCurrency(value, currency = "EUR") {
  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));
}

function formatInputDate(date = new Date()) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function formatDisplayDate(value) {
  if (!value) return "-";
  if (typeof value?.toDate === "function") {
    return value.toDate().toLocaleDateString("de-DE");
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString("de-DE");
}

export default function Payments({ t = {}, lang = "de" }) {
  const { currentTheme, theme } = usePanelTheme();
  const isGerman = lang === "de";
  const isAlbanian = lang === "sq";

  const text = {
    pageTitle:
      t.paymentsTitle ||
      (isGerman ? "Zahlungen" : isAlbanian ? "Pagesa" : "Payments"),
    pageSubtitle:
      t.paymentsSubtitle ||
      (isGerman
        ? "Verwalte Zahlungen, Status und Kategorien sauber."
        : isAlbanian
        ? "Menaxho pagesat, statuset dhe kategoritë në mënyrë të pastër."
        : "Manage payments, statuses and categories cleanly."),
    totalPayments:
      t.totalPayments ||
      (isGerman ? "Gesamtzahlungen" : isAlbanian ? "Totali i pagesave" : "Total payments"),
    thisMonth:
      t.thisMonth ||
      (isGerman ? "Dieser Monat" : isAlbanian ? "Ky muaj" : "This month"),
    paidCount:
      t.paidCount ||
      (isGerman ? "Bezahlt" : isAlbanian ? "Paguara" : "Paid"),
    pendingCount:
      t.pendingCount ||
      (isGerman ? "Offen" : isAlbanian ? "Në pritje" : "Pending"),
    addPayment:
      t.addPayment ||
      (isGerman ? "Zahlung hinzufügen" : isAlbanian ? "Shto pagesë" : "Add payment"),
    paymentHistory:
      t.paymentHistory ||
      (isGerman ? "Zahlungsverlauf" : isAlbanian ? "Historia e pagesave" : "Payment history"),
    choosePreset:
      t.choosePreset ||
      (isGerman ? "Vorlage wählen" : isAlbanian ? "Zgjidh listën" : "Choose preset"),
    noPresets:
      t.noPresets ||
      (isGerman ? "Keine Listen" : isAlbanian ? "Nuk ka lista" : "No lists"),
    customEntry:
      t.customEntry ||
      (isGerman ? "Manuell eingeben" : isAlbanian ? "Shkruaj manualisht" : "Write manually"),
    vendor:
      t.vendor ||
      (isGerman ? "Lieferant / Empfänger" : isAlbanian ? "Furnitori / Marrësi" : "Vendor / Receiver"),
    client:
      t.client ||
      (isGerman ? "Kunde" : isAlbanian ? "Klienti" : "Client"),
    noClient:
      t.noClient ||
      (isGerman ? "Kein Kunde" : isAlbanian ? "Pa klient" : "No client"),
    amount:
      t.amount ||
      (isGerman ? "Betrag" : isAlbanian ? "Shuma" : "Amount"),
    description:
      t.description ||
      (isGerman ? "Beschreibung" : isAlbanian ? "Përshkrimi" : "Description"),
    date:
      t.date ||
      (isGerman ? "Datum" : isAlbanian ? "Data" : "Date"),
    method:
      isGerman ? "Zahlungsmethode" : t.method || (isAlbanian ? "Metoda" : "Method"),
    status:
      t.status ||
      (isGerman ? "Status" : isAlbanian ? "Statusi" : "Status"),
    category:
      t.category ||
      (isGerman ? "Kategorie" : isAlbanian ? "Kategoria" : "Category"),
    savePayment:
      t.savePayment ||
      (isGerman ? "Zahlung speichern" : isAlbanian ? "Ruaj pagesën" : "Save payment"),
    loading:
      t.loading ||
      (isGerman ? "Wird geladen..." : isAlbanian ? "Duke u ngarkuar..." : "Loading..."),
    noData:
      t.noData ||
      (isGerman ? "Keine Daten" : isAlbanian ? "Nuk ka të dhëna" : "No data"),
    noDescription:
      t.noDescription ||
      (isGerman ? "Keine Beschreibung" : isAlbanian ? "Pa përshkrim" : "No description"),
    totalRegistered:
      t.totalRegistered ||
      (isGerman ? "Alle registrierten Zahlungen" : isAlbanian ? "Të gjitha pagesat e regjistruara" : "All registered payments"),
    monthlyPerformance:
      t.monthlyPerformance ||
      (isGerman ? "Monatliche Performance" : isAlbanian ? "Performanca mujore e pagesave" : "Monthly payment performance"),
    paidItems:
      t.paidItems ||
      (isGerman ? "Anzahl bezahlter Zahlungen" : isAlbanian ? "Numri i pagesave të paguara" : "Number of paid payments"),
    pendingItems:
      t.pendingItems ||
      (isGerman ? "Anzahl offener Zahlungen" : isAlbanian ? "Numri i pagesave në pritje" : "Number of pending payments"),
    historySubtitle:
      t.paymentsHistorySubtitle ||
      (isGerman
        ? "Sieh die letzten Zahlungen mit Status und Kategorie."
        : isAlbanian
        ? "Shiko pagesat e fundit me status dhe kategori."
        : "See recent payments with status and category."),
    amountPlaceholder:
      t.amountPlaceholder ||
      (isGerman ? "z. B. 120.00" : isAlbanian ? "P.sh. 120.00" : "e.g. 120.00"),
    vendorPlaceholder:
      t.vendorPlaceholder ||
      (isGerman ? "z. B. M&S BauHeld" : isAlbanian ? "P.sh. M&S BauHeld" : "e.g. M&S BauHeld"),
    descriptionPlaceholder:
      t.descriptionPlaceholder ||
      (isGerman ? "z. B. Monatliche Zahlung" : isAlbanian ? "P.sh. Pagesa mujore" : "e.g. Monthly payment"),
    paymentType:
      t.paymentType ||
      (isGerman ? "Zahlungstyp" : isAlbanian ? "Lloji i pagesës" : "Payment type"),
    worker:
      t.worker ||
      (isGerman ? "Mitarbeiter" : isAlbanian ? "Punëtori" : "Worker"),
    noWorker:
      t.noWorker ||
      (isGerman ? "Kein Mitarbeiter" : isAlbanian ? "Pa punëtor" : "No worker"),
    workerPayment:
      t.workerPayment ||
      (isGerman ? "Mitarbeiterlohn" : isAlbanian ? "Pagë punëtori" : "Worker salary"),
    supplierPayment:
      t.supplierPayment ||
      (isGerman ? "Lieferantenzahlung" : isAlbanian ? "Pagesë furnitori" : "Supplier payment"),
    officeRent:
      t.officeRent ||
      (isGerman ? "Miete" : isAlbanian ? "Qira" : "Rent"),
    materialPayment:
      t.materialPayment ||
      (isGerman ? "Material" : isAlbanian ? "Material" : "Material"),
    otherPayment:
      t.otherPayment ||
      (isGerman ? "Sonstige" : isAlbanian ? "Tjetër" : "Other"),
  };

  const presets = [
    {
      id: "rent",
      label: isGerman ? "Miete" : isAlbanian ? "Qira" : "Rent",
      subtitle: isGerman ? "Monatlich" : isAlbanian ? "Mujore" : "Monthly",
      category: isGerman ? "Büro" : isAlbanian ? "Zyrë" : "Office",
      vendor: "M&S BauHeld",
      description: isGerman ? "Monatliche Miete" : isAlbanian ? "Pagesa mujore" : "Monthly payment",
      amount: "",
      method: "Cash",
      status: "Paid",
    },
    {
      id: "supplier",
      label: isGerman ? "Lieferant" : isAlbanian ? "Furnitor" : "Supplier",
      subtitle: isGerman ? "Material" : isAlbanian ? "Material" : "Material",
      category: isGerman ? "Lieferant" : isAlbanian ? "Furnitor" : "Supplier",
      vendor: "",
      description: isGerman ? "Zahlung an Lieferant" : isAlbanian ? "Pagesë ndaj furnitorit" : "Supplier payment",
      amount: "",
      method: "Bank",
      status: "Pending",
    },
  ];

  const methodLabels = {
    Cash: isGerman ? "Bar" : isAlbanian ? "Cash" : "Cash",
    Bank: isGerman ? "Bank" : isAlbanian ? "Bankë" : "Bank",
    Transfer: isGerman ? "Überweisung" : isAlbanian ? "Transfer" : "Transfer",
    Card: isGerman ? "Karte" : isAlbanian ? "Kartë" : "Card",
  };

  const statusLabels = {
    Paid: isGerman ? "Bezahlt" : isAlbanian ? "Paguar" : "Paid",
    Pending: isGerman ? "Offen" : isAlbanian ? "Në pritje" : "Pending",
  };

  const paymentTypeOptions = [
    { value: "worker_salary", label: text.workerPayment },
    { value: "supplier", label: text.supplierPayment },
    { value: "rent", label: text.officeRent },
    { value: "material", label: text.materialPayment },
    { value: "other", label: text.otherPayment },
  ];

  const categoryLabelMap = {
    worker_salary: text.workerPayment,
    supplier: text.supplierPayment,
    rent: text.officeRent,
    material: text.materialPayment,
    other: text.otherPayment,
  };

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [mobileSection, setMobileSection] = useState("form");

  const [editId, setEditId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  const [form, setForm] = useState({
    presetId: "",
    clientId: "",
    client: "",
    vendor: "",
    amount: "",
    description: "",
    date: formatInputDate(),
    method: "Cash",
    status: "Paid",
    paymentType: "other",
    category: text.otherPayment,
    workerId: "",
    workerName: "",
  });

  const loadPayments = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(
        query(collection(db, "payments"), orderBy("createdAt", "desc"))
      );
      setItems(snap.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
    } catch (error) {
      console.error("Error loading payments:", error);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const loadClients = async () => {
    try {
      const snap = await getDocs(
        query(
          collection(db, "invoice_clients"),
          orderBy("createdAt", "desc"),
          limit(50)
        )
      );
      setClients(snap.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
    } catch (error) {
      console.error("Error loading clients for payments:", error);
      setClients([]);
    }
  };

  const loadWorkers = async () => {
    try {
      const snap = await getDocs(
        query(collection(db, "workers"), orderBy("createdAt", "desc"), limit(100))
      );
      setWorkers(snap.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
    } catch (error) {
      console.error("Error loading workers for payments:", error);
      setWorkers([]);
    }
  };

  useEffect(() => {
    loadPayments();
    loadClients();
    loadWorkers();
  }, []);

  useEffect(() => {
    const presetRaw = localStorage.getItem("bauheld:selectedClientPreset");
    if (!presetRaw) return;

    try {
      const parsed = JSON.parse(presetRaw);
      if (parsed?.id) {
        setForm((prev) => ({
          ...prev,
          clientId: parsed.id || "",
          client: parsed.companyName || parsed.name || "",
          vendor: prev.vendor || parsed.companyName || parsed.name || "",
        }));
        setMobileSection("form");
      }
    } catch (error) {
      console.error("Error reading selected client preset:", error);
    } finally {
      localStorage.removeItem("bauheld:selectedClientPreset");
    }
  }, []);

  const totals = useMemo(() => {
    const now = new Date();
    return items.reduce(
      (acc, item) => {
        const amount = Number(item.amount || 0);
        acc.total += amount;

        const dateValue =
          typeof item.date?.toDate === "function"
            ? item.date.toDate()
            : new Date(item.date || item.createdAt || 0);

        if (
          !Number.isNaN(dateValue.getTime()) &&
          dateValue.getMonth() === now.getMonth() &&
          dateValue.getFullYear() === now.getFullYear()
        ) {
          acc.month += amount;
        }

        if (String(item.status || "").toLowerCase() === "paid") acc.paid += 1;
        else acc.pending += 1;

        return acc;
      },
      { total: 0, month: 0, paid: 0, pending: 0 }
    );
  }, [items]);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = items.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.max(1, Math.ceil(items.length / itemsPerPage));

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const inputClass = `h-12 w-full rounded-2xl border px-4 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 ${
    theme === "dark"
      ? "border-slate-700 bg-slate-900 text-slate-100 placeholder:text-slate-500"
      : "border-slate-200 bg-slate-50 text-slate-900 focus:bg-white"
  }`;

  const labelClass = `mb-2 block text-sm font-semibold ${
    theme === "dark" ? "text-slate-200" : "text-slate-700"
  }`;

  const getStatusBadge = (status) => {
    const value = String(status || "").toLowerCase();
    if (value === "paid") {
      return theme === "dark"
        ? "inline-flex rounded-full bg-emerald-950 px-2.5 py-1 text-xs font-semibold text-emerald-300"
        : "inline-flex rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-semibold text-emerald-700";
    }
    return theme === "dark"
      ? "inline-flex rounded-full bg-amber-950 px-2.5 py-1 text-xs font-semibold text-amber-300"
      : "inline-flex rounded-full bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-700";
  };

  const getMethodBadge = (method) => {
    const value = String(method || "").toLowerCase();
    if (value === "cash") {
      return theme === "dark"
        ? "inline-flex rounded-full bg-emerald-950 px-2.5 py-1 text-xs font-semibold text-emerald-300"
        : "inline-flex rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-semibold text-emerald-700";
    }
    if (value === "bank" || value === "transfer") {
      return theme === "dark"
        ? "inline-flex rounded-full bg-blue-950 px-2.5 py-1 text-xs font-semibold text-blue-300"
        : "inline-flex rounded-full bg-blue-100 px-2.5 py-1 text-xs font-semibold text-blue-700";
    }
    return theme === "dark"
      ? "inline-flex rounded-full bg-slate-800 px-2.5 py-1 text-xs font-semibold text-slate-300"
      : "inline-flex rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-700";
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleClientSelect = (clientId) => {
    if (!clientId) {
      setForm((prev) => ({
        ...prev,
        clientId: "",
        client: "",
      }));
      return;
    }

    const found = clients.find((c) => c.id === clientId);
    if (!found) return;

    const clientName = found.companyName || found.name || "";
    setForm((prev) => ({
      ...prev,
      clientId,
      client: clientName,
      vendor: prev.vendor || clientName,
    }));
  };

  const handlePresetChange = (value) => {
    if (!value) {
      setForm((prev) => ({ ...prev, presetId: "" }));
      return;
    }

    const preset = presets.find((p) => p.id === value);
    if (!preset) return;

    setForm((prev) => ({
      ...prev,
      presetId: preset.id,
      vendor: preset.vendor || prev.vendor || "",
      amount: preset.amount || "",
      description: preset.description || "",
      method: preset.method || "Cash",
      status: preset.status || "Paid",
      category: preset.category || prev.category,
    }));
  };

  const handlePaymentTypeChange = (value) => {
    const fallbackCategory = categoryLabelMap[value] || text.otherPayment;

    setForm((prev) => {
      if (value === "worker_salary") {
        const selectedWorker = workers.find((worker) => worker.id === prev.workerId);
        const workerName = selectedWorker?.name || prev.workerName || "";
        return {
          ...prev,
          paymentType: value,
          category: fallbackCategory,
          vendor: workerName || prev.vendor || "",
          workerName,
        };
      }

      return {
        ...prev,
        paymentType: value,
        category: fallbackCategory,
        workerId: "",
        workerName: "",
      };
    });
  };

  const handleWorkerSelect = (workerId) => {
    if (!workerId) {
      setForm((prev) => ({
        ...prev,
        workerId: "",
        workerName: "",
        vendor: prev.paymentType === "worker_salary" ? "" : prev.vendor,
      }));
      return;
    }

    const found = workers.find((worker) => worker.id === workerId);
    if (!found) return;

    setForm((prev) => ({
      ...prev,
      workerId,
      workerName: found.name || "",
      vendor: prev.paymentType === "worker_salary" ? found.name || "" : prev.vendor,
      description:
        prev.paymentType === "worker_salary" && !prev.description
          ? `${text.workerPayment} - ${found.name || ""}`
          : prev.description,
    }));
  };

  const resetForm = () => {
    setForm({
      presetId: "",
      clientId: "",
      client: "",
      vendor: "",
      amount: "",
      description: "",
      date: formatInputDate(),
      method: "Cash",
      status: "Paid",
      paymentType: "other",
      category: text.otherPayment,
      workerId: "",
      workerName: "",
    });
    setEditId(null);
  };

  const handleEdit = (item) => {
    setEditId(item.id);
    setForm({
      presetId: "",
      clientId: item.clientId || "",
      client: item.client || item.customer || "",
      vendor: item.vendor || item.client || item.customer || item.name || "",
      amount: item.amount || "",
      description: item.description || "",
      date: item.date || formatInputDate(),
      method: item.method || "Cash",
      status: item.status || "Paid",
      paymentType: item.paymentType || (item.workerId ? "worker_salary" : "other"),
      category: item.category || "",
      workerId: item.workerId || "",
      workerName: item.workerName || "",
    });
    setMobileSection("form");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (id) => {
    if (
      !window.confirm(
        isGerman
          ? "Möchten Sie das wirklich löschen?"
          : "A jeni i sigurt që doni ta fshini?"
      )
    )
      return;

    try {
      await deletePaymentWithStats(id);
      notify.success(isGerman ? "Erfolgreich gelöscht" : "U fshi me sukses");
      if (editId === id) resetForm();
      await loadPayments();
      if (currentItems.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      }
    } catch (error) {
      console.error("Error deleting payment:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.amount || Number(form.amount) <= 0) return;
    if (form.paymentType === "worker_salary" && !form.workerId) {
      notify.error(isGerman ? "Bitte Mitarbeiter auswählen." : "Zgjidh punëtorin.");
      return;
    }

    setSaving(true);
    try {
      const resolvedVendor =
        form.paymentType === "worker_salary"
          ? form.workerName || form.vendor || ""
          : form.vendor || "";

      const entry = {
        vendor: resolvedVendor,
        amount: Number(form.amount || 0),
        description: form.description || "",
        date: form.date || formatInputDate(),
        method: form.method || "Cash",
        status: form.status || "Paid",
        paymentType: form.paymentType || "other",
        category: form.category || categoryLabelMap[form.paymentType] || "",
        client: form.client || "",
        clientId: form.clientId || "",
        workerId: form.paymentType === "worker_salary" ? form.workerId || "" : "",
        workerName: form.paymentType === "worker_salary" ? form.workerName || "" : "",
      };

      if (editId) {
        await updatePaymentWithStats(editId, entry);
        notify.success(
          t.updateSuccess ||
            (isGerman ? "Erfolgreich aktualisiert" : "U përditësua me sukses")
        );
      } else {
        await createPaymentWithStats(entry);
        notify.success(
          t.savedSuccessfully || (isGerman ? "Zahlung gespeichert" : "Pagesa u ruajt me sukses")
        );
      }

      resetForm();
      await loadPayments();
      setCurrentPage(1);
    } catch (error) {
      console.error("Error saving payment:", error);
    } finally {
      setSaving(false);
    }
  };

  const historyTitle =
    t.paymentHistory || (isGerman ? "Zahlungsverlauf" : "Historia e pagesave");
  const recordsLabel =
    t.records || (isGerman ? "Einträge" : "regjistrime");
  const prevLabel = t.prev || (isGerman ? "Zurück" : "Mbrapa");
  const nextLabel = t.next || (isGerman ? "Weiter" : "Para");
  const pageLabel = t.page || (isGerman ? "Seite" : "Faqja");
  const ofLabel = t.of || (isGerman ? "von" : "nga");

  return (
    <PageShell>
      <SectionHeading title={text.pageTitle} subtitle={text.pageSubtitle} />

      <div className="grid min-w-0 grid-cols-2 gap-2.5 xl:grid-cols-4">
        <StatCard
          title={text.totalPayments}
          value={formatCurrency(totals.total)}
          subtitle={text.totalRegistered}
          icon={Wallet}
          accent="blue"
        />
        <StatCard
          title={text.thisMonth}
          value={formatCurrency(totals.month)}
          subtitle={text.monthlyPerformance}
          icon={CalendarDays}
          accent="emerald"
        />
        <StatCard
          title={text.paidCount}
          value={totals.paid}
          subtitle={text.paidItems}
          icon={CheckCircle2}
          accent="violet"
        />
        <StatCard
          title={text.pendingCount}
          value={totals.pending}
          subtitle={text.pendingItems}
          icon={Clock3}
          accent="amber"
        />
      </div>

      <div className="no-print xl:hidden">
        <PanelCard className="p-2">
          <div className="grid grid-cols-2 gap-2">
            <UIButton
              type="button"
              onClick={() => setMobileSection("form")}
              theme={theme}
              variant={mobileSection === "form" ? "primary" : "secondary"}
              size="lg"
              className="w-full"
            >
              {editId ? (t.update || "Përditëso") : text.addPayment}
            </UIButton>

            <UIButton
              type="button"
              onClick={() => setMobileSection("history")}
              theme={theme}
              variant={mobileSection === "history" ? "primary" : "secondary"}
              size="lg"
              className="w-full"
            >
              {text.paymentHistory}
            </UIButton>
          </div>
        </PanelCard>
      </div>

      <div className="grid gap-5 xl:grid-cols-[0.95fr_1.55fr]">
        <div className={mobileSection === "history" ? "hidden xl:block" : "block"}>
          <PanelCard>
            <div className="mb-5 flex items-center justify-between">
              <div>
                <h3
                  className={`text-2xl font-bold ${
                    theme === "dark" ? "text-slate-100" : "text-slate-900"
                  }`}
                >
                  {editId ? (t.update || "Përditëso") : text.addPayment}
                </h3>
                <p className={`mt-2 text-sm ${currentTheme.muted}`}>
                  {text.pageSubtitle}
                </p>
              </div>

              {editId && (
                <UIButton
                  type="button"
                  onClick={resetForm}
                  theme={theme}
                  variant="softDanger"
                  size="sm"
                >
                  {t.cancel || "Anulo"}
                </UIButton>
              )}
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className={labelClass}>{text.choosePreset}</label>
                <select
                  value={form.presetId}
                  onChange={(e) => handlePresetChange(e.target.value)}
                  className={inputClass}
                >
                  <option value="">
                    {presets.length ? text.customEntry : text.noPresets}
                  </option>
                  {presets.map((preset) => (
                    <option key={preset.id} value={preset.id}>
                      {preset.label}
                      {preset.subtitle ? ` · ${preset.subtitle}` : ""}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className={labelClass}>{text.client}</label>
                <select
                  value={form.clientId}
                  onChange={(e) => handleClientSelect(e.target.value)}
                  className={inputClass}
                >
                  <option value="">{text.noClient}</option>
                  {clients.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.companyName || c.name || "-"}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className={labelClass}>{text.paymentType}</label>
                  <select
                    value={form.paymentType}
                    onChange={(e) => handlePaymentTypeChange(e.target.value)}
                    className={inputClass}
                  >
                    {paymentTypeOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                {form.paymentType === "worker_salary" ? (
                  <div>
                    <label className={labelClass}>{text.worker}</label>
                    <select
                      value={form.workerId}
                      onChange={(e) => handleWorkerSelect(e.target.value)}
                      className={inputClass}
                    >
                      <option value="">{text.noWorker}</option>
                      {workers.map((worker) => (
                        <option key={worker.id} value={worker.id}>
                          {worker.name || "-"}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : (
                  <div>
                    <label className={labelClass}>{text.category}</label>
                    <input
                      type="text"
                      value={form.category}
                      onChange={(e) => handleChange("category", e.target.value)}
                      placeholder={
                        isGerman
                          ? "z. B. Lieferant"
                          : isAlbanian
                          ? "P.sh. Furnitor"
                          : "e.g. Supplier"
                      }
                      className={inputClass}
                    />
                  </div>
                )}
              </div>

              <div>
                <label className={labelClass}>{text.vendor}</label>
                <input
                  type="text"
                  value={form.vendor}
                  onChange={(e) => handleChange("vendor", e.target.value)}
                  placeholder={text.vendorPlaceholder}
                  className={inputClass}
                />
              </div>

              <div>
                <label className={labelClass}>{text.amount}</label>
                <input
                  type="number"
                  step="0.01"
                  value={form.amount}
                  onChange={(e) => handleChange("amount", e.target.value)}
                  placeholder={text.amountPlaceholder}
                  className={inputClass}
                />
              </div>

              <div>
                <label className={labelClass}>{text.description}</label>
                <input
                  type="text"
                  value={form.description}
                  onChange={(e) => handleChange("description", e.target.value)}
                  placeholder={text.descriptionPlaceholder}
                  className={inputClass}
                />
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className={labelClass}>{text.date}</label>
                  <input
                    type="date"
                    value={form.date}
                    onChange={(e) => handleChange("date", e.target.value)}
                    className={inputClass}
                  />
                </div>

                <div>
                  <label className={labelClass}>{text.method}</label>
                  <select
                    value={form.method}
                    onChange={(e) => handleChange("method", e.target.value)}
                    className={inputClass}
                  >
                    <option value="Cash">{methodLabels.Cash}</option>
                    <option value="Bank">{methodLabels.Bank}</option>
                    <option value="Transfer">{methodLabels.Transfer}</option>
                    <option value="Card">{methodLabels.Card}</option>
                  </select>
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className={labelClass}>{text.status}</label>
                  <select
                    value={form.status}
                    onChange={(e) => handleChange("status", e.target.value)}
                    className={inputClass}
                  >
                    <option value="Paid">{statusLabels.Paid}</option>
                    <option value="Pending">{statusLabels.Pending}</option>
                  </select>
                </div>

                <div>
                  <label className={labelClass}>{text.category}</label>
                  <div className={`${inputClass} flex items-center ${form.paymentType === "worker_salary" ? "opacity-70" : ""}`}>
                    <span>{form.category || categoryLabelMap[form.paymentType] || "—"}</span>
                  </div>
                </div>
              </div>

              <UIButton
                type="submit"
                disabled={saving}
                theme={theme}
                variant="primary"
                size="xl"
                className="w-full"
              >
                {saving ? text.loading : editId ? (t.update || "Përditëso") : text.savePayment}
              </UIButton>
            </form>
          </PanelCard>
        </div>

        <div className={mobileSection === "form" ? "hidden xl:block" : "block"}>
          <PanelCard>
            <div className="mb-5 flex items-start justify-between gap-4">
              <div>
                <h3
                  className={`text-2xl font-bold ${
                    theme === "dark" ? "text-slate-100" : "text-slate-900"
                  }`}
                >
                  {historyTitle}
                </h3>
                <p className={`mt-2 text-sm ${currentTheme.muted}`}>
                  {text.historySubtitle}
                </p>
              </div>
              <div className={`text-sm font-semibold ${currentTheme.muted}`}>
                {items.length} {recordsLabel}
              </div>
            </div>

            <div className="flex-1">
              {loading ? (
                <div className={`py-10 text-sm ${currentTheme.muted}`}>{text.loading}</div>
              ) : currentItems.length === 0 ? (
                <div className={`py-10 text-sm ${currentTheme.muted}`}>{text.noData}</div>
              ) : (
                <div className="space-y-3">
                  {currentItems.map((item, index) => (
                    <div
                      key={item.id || index}
                      className={`flex flex-col gap-4 rounded-3xl border p-4 transition shadow-[0_12px_30px_rgba(15,23,42,0.04)] md:flex-row md:items-center md:justify-between ${
                        theme === "dark"
                          ? "border-slate-700 bg-slate-900/60 hover:border-slate-600 hover:bg-slate-900"
                          : "border-slate-200 bg-slate-50/80 hover:border-slate-300 hover:bg-white"
                      }`}
                    >
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <h4
                            className={`text-base font-semibold ${
                              theme === "dark" ? "text-slate-100" : "text-slate-900"
                            }`}
                          >
                            {formatCurrency(item.amount)}
                          </h4>

                          <span className={getStatusBadge(item.status)}>
                            {statusLabels[item.status] || item.status || "—"}
                          </span>

                          <span className={getMethodBadge(item.method)}>
                            {methodLabels[item.method] || item.method || "—"}
                          </span>

                          {item.category ? (
                            <span
                              className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${
                                theme === "dark"
                                  ? "bg-sky-950 text-sky-300"
                                  : "bg-sky-100 text-sky-700"
                              }`}
                            >
                              {item.category}
                            </span>
                          ) : null}

                          {item.client ? (
                            <span
                              className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${
                                theme === "dark"
                                  ? "bg-violet-950 text-violet-300"
                                  : "bg-violet-100 text-violet-700"
                              }`}
                            >
                              {item.client}
                            </span>
                          ) : null}

                          {item.workerName ? (
                            <span
                              className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${
                                theme === "dark"
                                  ? "bg-rose-950 text-rose-300"
                                  : "bg-rose-100 text-rose-700"
                              }`}
                            >
                              {item.workerName}
                            </span>
                          ) : null}
                        </div>

                        <p
                          className={`mt-1 text-sm font-medium ${
                            theme === "dark" ? "text-slate-300" : "text-slate-700"
                          }`}
                        >
                          {item.vendor || item.client || item.name || "—"}
                        </p>

                        <p
                          className={`mt-1 truncate text-sm ${
                            theme === "dark" ? "text-slate-400" : "text-slate-600"
                          }`}
                        >
                          {item.description || text.noDescription}
                        </p>
                      </div>

                      <div className="mt-2 flex flex-col gap-2 md:mt-0 md:items-end">
                        <div className={`text-sm font-medium ${currentTheme.muted}`}>
                          {formatDisplayDate(item.date || item.createdAt)}
                        </div>

                        <div className="flex items-center gap-2">
                          <UIButton
                            type="button"
                            onClick={() => handleEdit(item)}
                            theme={theme}
                            variant="primary"
                            size="sm"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                            <span className="hidden sm:inline">{t.edit || "Ndrysho"}</span>
                          </UIButton>

                          <UIButton
                            type="button"
                            onClick={() => handleDelete(item.id)}
                            theme={theme}
                            variant="softDanger"
                            size="sm"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                            <span className="hidden sm:inline">{t.delete || "Fshij"}</span>
                          </UIButton>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {totalPages > 1 && (
              <div className="mt-6 flex items-center justify-between border-t border-slate-200/60 pt-5 dark:border-slate-700/60">
                <UIButton
                  type="button"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  theme={theme}
                  variant="secondary"
                  size="md"
                >
                  <ChevronLeft className="h-4 w-4" />
                  <span className="hidden sm:inline">{prevLabel}</span>
                </UIButton>

                <span className={`text-sm font-semibold ${currentTheme.muted}`}>
                  {pageLabel} {currentPage} {ofLabel} {totalPages}
                </span>

                <UIButton
                  type="button"
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  theme={theme}
                  variant="secondary"
                  size="md"
                >
                  <span className="hidden sm:inline">{nextLabel}</span>
                  <ChevronRight className="h-4 w-4" />
                </UIButton>
              </div>
            )}
          </PanelCard>
        </div>
      </div>
    </PageShell>
  );
}
